import type { IPaymentV2Repository, PaginatedResponse } from '@yawi/domain';
import { paginateArray } from '@yawi/domain';
import {
  PaymentMethod,
  PaymentStatus,
  type Payment,
  type PaymentSummary,
  type UploadPaymentProofDTO,
  type VerifyPaymentDTO,
  type CreateManualPaymentDTO,
  type UpdateManualPaymentDTO,
  type PaymentQueryParams,
} from '@yawi/types';

// Mock data - pagos de prueba
const mockPayments: Payment[] = [
  // En verificación - recién subido
  {
    id: 'pay-001',
    memberId: 'user-001',
    unitNumber: '101',
    amount: 2500,
    method: PaymentMethod.TRANSFER,
    status: PaymentStatus.VERIFYING,
    proofUrl: 'https://picsum.photos/seed/proof1/400/600',
    transactionReference: 'SPEI-123456789',
    notes: 'Pago cuota diciembre',
    paidAt: '2025-12-02T14:30:00Z',
    verifiedAt: null,
    verifiedBy: null,
    rejectionReason: null,
    invoiceUuid: null,
    xmlUrl: null,
    pdfUrl: null,
    invoicedAt: null,
    allocations: [
      {
        id: 'alloc-001',
        chargeId: 'chg-001',
        amount: 2500,
        chargeConcept: 'Cuota de Mantenimiento',
        chargePeriod: '2025-12',
        createdAt: '2025-12-02T14:30:00Z',
      },
    ],
    createdAt: '2025-12-02T14:30:00Z',
    updatedAt: '2025-12-02T14:30:00Z',
    allocatedAmount: 2500,
    unallocatedAmount: 0,
  },
  // Verificado - Octubre 2025
  {
    id: 'pay-002',
    memberId: 'user-001',
    unitNumber: '101',
    amount: 2500,
    method: PaymentMethod.TRANSFER,
    status: PaymentStatus.VERIFIED,
    proofUrl: 'https://picsum.photos/seed/proof2/400/600',
    transactionReference: 'SPEI-987654321',
    notes: null,
    paidAt: '2025-10-10T09:15:00Z',
    verifiedAt: '2025-10-10T11:00:00Z',
    verifiedBy: 'Administrador',
    rejectionReason: null,
    invoiceUuid: null,
    xmlUrl: null,
    pdfUrl: null,
    invoicedAt: null,
    allocations: [
      {
        id: 'alloc-002',
        chargeId: 'chg-005',
        amount: 2500,
        chargeConcept: 'Cuota de Mantenimiento',
        chargePeriod: '2025-10',
        createdAt: '2025-10-10T09:15:00Z',
      },
    ],
    createdAt: '2025-10-10T09:15:00Z',
    updatedAt: '2025-10-10T11:00:00Z',
    allocatedAmount: 2500,
    unallocatedAmount: 0,
  },
  // Verificado - Septiembre 2025
  {
    id: 'pay-003',
    memberId: 'user-001',
    unitNumber: '101',
    amount: 2500,
    method: PaymentMethod.CARD,
    status: PaymentStatus.VERIFIED,
    proofUrl: null,
    transactionReference: 'CARD-555666777',
    notes: 'Pago con tarjeta en la oficina',
    paidAt: '2025-09-12T16:45:00Z',
    verifiedAt: '2025-09-12T16:45:00Z',
    verifiedBy: 'Sistema',
    rejectionReason: null,
    invoiceUuid: 'UUID-12345-ABCDE',
    xmlUrl: 'https://example.com/invoice.xml',
    pdfUrl: 'https://example.com/invoice.pdf',
    invoicedAt: '2025-09-13T10:00:00Z',
    allocations: [
      {
        id: 'alloc-003',
        chargeId: 'chg-006',
        amount: 2500,
        chargeConcept: 'Cuota de Mantenimiento',
        chargePeriod: '2025-09',
        createdAt: '2025-09-12T16:45:00Z',
      },
    ],
    createdAt: '2025-09-12T16:45:00Z',
    updatedAt: '2025-09-13T10:00:00Z',
    allocatedAmount: 2500,
    unallocatedAmount: 0,
  },
  // Verificado - Agosto 2025
  {
    id: 'pay-004',
    memberId: 'user-001',
    unitNumber: '101',
    amount: 2500,
    method: PaymentMethod.CASH,
    status: PaymentStatus.VERIFIED,
    proofUrl: null,
    transactionReference: null,
    notes: 'Pago en efectivo',
    paidAt: '2025-08-14T10:00:00Z',
    verifiedAt: '2025-08-14T10:00:00Z',
    verifiedBy: 'Administrador',
    rejectionReason: null,
    invoiceUuid: null,
    xmlUrl: null,
    pdfUrl: null,
    invoicedAt: null,
    allocations: [
      {
        id: 'alloc-004',
        chargeId: 'chg-007',
        amount: 2500,
        chargeConcept: 'Cuota de Mantenimiento',
        chargePeriod: '2025-08',
        createdAt: '2025-08-14T10:00:00Z',
      },
    ],
    createdAt: '2025-08-14T10:00:00Z',
    updatedAt: '2025-08-14T10:00:00Z',
    allocatedAmount: 2500,
    unallocatedAmount: 0,
  },
  // Pago parcial a derrama - Verificado
  {
    id: 'pay-005',
    memberId: 'user-001',
    unitNumber: '101',
    amount: 2000,
    method: PaymentMethod.TRANSFER,
    status: PaymentStatus.VERIFIED,
    proofUrl: 'https://picsum.photos/seed/proof5/400/600',
    transactionReference: 'SPEI-111222333',
    notes: 'Abono a derrama del elevador',
    paidAt: '2025-11-20T08:30:00Z',
    verifiedAt: '2025-11-20T12:00:00Z',
    verifiedBy: 'Administrador',
    rejectionReason: null,
    invoiceUuid: null,
    xmlUrl: null,
    pdfUrl: null,
    invoicedAt: null,
    allocations: [
      {
        id: 'alloc-005',
        chargeId: 'chg-003',
        amount: 2000,
        chargeConcept: 'Derrama - Reparación Elevador',
        chargePeriod: null,
        createdAt: '2025-11-20T08:30:00Z',
      },
    ],
    createdAt: '2025-11-20T08:30:00Z',
    updatedAt: '2025-11-20T12:00:00Z',
    allocatedAmount: 2000,
    unallocatedAmount: 0,
  },
  // Rechazado
  {
    id: 'pay-006',
    memberId: 'user-001',
    unitNumber: '101',
    amount: 2500,
    method: PaymentMethod.TRANSFER,
    status: PaymentStatus.REJECTED,
    proofUrl: 'https://picsum.photos/seed/proof6/400/600',
    transactionReference: 'SPEI-INVALID',
    notes: 'Pago noviembre',
    paidAt: '2025-11-16T15:00:00Z',
    verifiedAt: '2025-11-17T09:00:00Z',
    verifiedBy: 'Administrador',
    rejectionReason: 'Comprobante ilegible. Por favor suba una imagen más clara.',
    invoiceUuid: null,
    xmlUrl: null,
    pdfUrl: null,
    invoicedAt: null,
    allocations: [],
    createdAt: '2025-11-16T15:00:00Z',
    updatedAt: '2025-11-17T09:00:00Z',
    allocatedAmount: 0,
    unallocatedAmount: 2500,
  },
];

export class MockPaymentV2Repository implements IPaymentV2Repository {
  // === Admin Queries ===

  async getAllPayments(params?: PaymentQueryParams): Promise<PaginatedResponse<Payment>> {
    let filtered = [...mockPayments];

    if (params?.status) {
      filtered = filtered.filter(p => p.status === params.status);
    }
    if (params?.method) {
      filtered = filtered.filter(p => p.method === params.method);
    }

    return paginateArray(filtered, params?.page || 1, params?.limit || 10);
  }

  async getPendingVerification(): Promise<Payment[]> {
    return mockPayments.filter(p => p.status === PaymentStatus.VERIFYING);
  }

  async getSummary(): Promise<PaymentSummary> {
    const verifying = mockPayments.filter(p => p.status === PaymentStatus.VERIFYING);
    const verified = mockPayments.filter(p => p.status === PaymentStatus.VERIFIED);
    const rejected = mockPayments.filter(p => p.status === PaymentStatus.REJECTED);

    return {
      totalVerifying: verifying.length,
      totalVerified: verified.length,
      totalRejected: rejected.length,
      amountVerified: verified.reduce((sum, p) => sum + p.amount, 0),
      amountPending: verifying.reduce((sum, p) => sum + p.amount, 0),
    };
  }

  async getPaymentById(id: string): Promise<Payment | null> {
    return mockPayments.find(p => p.id === id) || null;
  }

  async getMemberPayments(memberId: string, params?: PaymentQueryParams): Promise<PaginatedResponse<Payment>> {
    const memberPayments = mockPayments.filter(p => p.memberId === memberId);
    return paginateArray(memberPayments, params?.page || 1, params?.limit || 10);
  }

  // === Resident Queries ===

  async getMyPayments(params?: PaymentQueryParams): Promise<PaginatedResponse<Payment>> {
    // Return all payments for the mock user
    return paginateArray(mockPayments, params?.page || 1, params?.limit || 10);
  }

  // === Commands ===

  async uploadProof(_data: UploadPaymentProofDTO): Promise<Payment> {
    throw new Error('Mock: uploadProof not implemented');
  }

  async verifyPayment(_id: string, _data: VerifyPaymentDTO): Promise<Payment> {
    throw new Error('Mock: verifyPayment not implemented');
  }

  async createManualPayment(data: CreateManualPaymentDTO): Promise<Payment> {
    const now = new Date().toISOString();
    const allocatedAmount = data.allocations.reduce((sum, a) => sum + a.amount, 0);
    const payment: Payment = {
      id: `pay-manual-${Date.now()}`,
      memberId: data.memberId,
      amount: data.amount,
      method: data.method,
      status: PaymentStatus.VERIFIED,
      proofUrl: null,
      transactionReference: data.transactionReference ?? null,
      notes: data.notes ?? null,
      paidAt: data.paidAt ?? now,
      verifiedAt: now,
      verifiedBy: 'Administrador',
      rejectionReason: null,
      invoiceUuid: null,
      xmlUrl: null,
      pdfUrl: null,
      invoicedAt: null,
      allocations: data.allocations.map((a, i) => ({
        id: `alloc-manual-${Date.now()}-${i}`,
        chargeId: a.chargeId,
        amount: a.amount,
        createdAt: now,
      })),
      createdAt: now,
      updatedAt: now,
      allocatedAmount,
      unallocatedAmount: data.amount - allocatedAmount,
    };
    mockPayments.unshift(payment);
    return payment;
  }

  async updateManualPayment(id: string, data: UpdateManualPaymentDTO): Promise<Payment> {
    const index = mockPayments.findIndex(p => p.id === id);
    if (index === -1) {
      throw new Error('Mock: payment not found');
    }
    const current = mockPayments[index];
    if (current.status !== PaymentStatus.VERIFIED) {
      throw new Error('Mock: solo se pueden editar pagos manuales verificados');
    }

    const now = new Date().toISOString();
    const allocatedAmount = data.allocations?.reduce((sum, a) => sum + a.amount, 0)
      ?? current.allocatedAmount;

    const updated: Payment = {
      ...current,
      memberId: data.memberId ?? current.memberId,
      amount: data.amount ?? current.amount,
      method: data.method ?? current.method,
      transactionReference: data.transactionReference !== undefined
        ? data.transactionReference
        : current.transactionReference,
      notes: data.notes !== undefined ? data.notes : current.notes,
      paidAt: data.paidAt ?? current.paidAt,
      proofUrl: data.proofUrl !== undefined ? data.proofUrl : current.proofUrl,
      allocations: data.allocations
        ? data.allocations.map((a, i) => ({
            id: `alloc-manual-${Date.now()}-${i}`,
            chargeId: a.chargeId,
            amount: a.amount,
            createdAt: now,
          }))
        : current.allocations,
      updatedAt: now,
      allocatedAmount,
      unallocatedAmount: (data.amount ?? current.amount) - allocatedAmount,
    };
    mockPayments[index] = updated;
    return updated;
  }

  async deleteManualPayment(id: string): Promise<void> {
    const index = mockPayments.findIndex(p => p.id === id);
    if (index !== -1) {
      mockPayments.splice(index, 1);
    }
  }

  async deletePayment(_id: string): Promise<void> {
    // No-op for mock
  }
}
